import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { FlatList } from "react-native";
import GalleryThumbnail from "./Thumbnail";
import { GalleryThumbnailRailProps } from "./types";

const GalleryThumbnailRail: React.FC<GalleryThumbnailRailProps> = React.memo(
  ({
    currentIndex,
    imageSwiperRef,
    isSmallVersion,
    scaleAll,
    styles,
    thumbImages,
  }) => {
    const listRef = useRef<FlatList<string> | null>(null);

    const itemStep = useMemo(() => {
      const cardWidth = (isSmallVersion ? 74 : 96) * scaleAll;
      const pressPadding = 8 * scaleAll;
      const itemGap = 10 * scaleAll;

      return cardWidth + pressPadding + itemGap;
    }, [isSmallVersion, scaleAll]);

    const keyExtractor = useCallback((item: string, index: number) => `${item}_${index}`, []);

    const getItemLayout = useCallback(
      (_: ArrayLike<string> | null | undefined, index: number) => ({
        length: itemStep,
        offset: itemStep * index,
        index,
      }),
      [itemStep]
    );

    const handleScrollToIndexFailed = useCallback((info: {
      averageItemLength: number;
      index: number;
    }) => {
      listRef.current?.scrollToOffset({
        offset: info.averageItemLength * info.index,
        animated: true,
      });

      setTimeout(() => {
        listRef.current?.scrollToIndex({
          index: info.index,
          animated: true,
          viewPosition: 0.5,
        });
      }, 100);
    }, []);

    const renderItem = useCallback(
      ({ item, index }: { item: string; index: number }) => (
        <GalleryThumbnail
          image={item}
          index={index}
          isActive={currentIndex === index}
          imageSwiperRef={imageSwiperRef}
          isSmallVersion={isSmallVersion}
          styles={styles}
        />
      ),
      [currentIndex, imageSwiperRef, isSmallVersion, styles]
    );

    useEffect(() => {
      listRef.current?.scrollToIndex({
        index: currentIndex,
        animated: true,
        viewPosition: 0.5,
      });
    }, [currentIndex]);

    if (thumbImages.length <= 1) return null;

    return (
      <FlatList
        ref={listRef}
        horizontal
        data={thumbImages}
        extraData={currentIndex}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.thumbRail}
        style={styles.thumbRailScroll}
        keyExtractor={keyExtractor}
        getItemLayout={getItemLayout}
        onScrollToIndexFailed={handleScrollToIndexFailed}
        initialNumToRender={Math.min(thumbImages.length, 6)}
        maxToRenderPerBatch={6}
        windowSize={5}
        removeClippedSubviews={false}
        renderItem={renderItem}
      />
    );
  }
);

export default GalleryThumbnailRail;
